import { useEffect, useState } from 'react';

import { mutations } from 'entities';

import { Box, Button, Column, Input, Row } from 'components/Blocks';
import { CheckIcon, MailIcon } from 'components/Blocks/Icons';

import { ResendButtonCode } from './ResendButton';

import { useAuth, useUser } from 'providers/Auth';
import { useLang } from 'locale';

interface ConfirmEmailFormProps {
  onConfirm: () => void;
  rewardPoints: number;
}

const CODE_LENGTH = 6;
const RESEND_SECONDS = 185;

export function ConfirmEmailForm({ onConfirm, rewardPoints }: ConfirmEmailFormProps) {
  const { t } = useLang();
  const { activeIncentive } = useUser();
  const { updateUser } = useAuth();

  const [code, setCode] = useState('');
  const [seconds, setSeconds] = useState(RESEND_SECONDS);

  const codeOk = code.length === CODE_LENGTH;

  const confirmEmail = mutations.users.confirmEmail.use({
    block: !codeOk,
    params: { code },
    onSuccess: () => {
      updateUser({
        activeIncentive: activeIncentive + rewardPoints,
      });
      onConfirm();
    },
  });

  const sendCode = mutations.users.sendEmailConfirmation.use({
    onSuccess: () => setSeconds(RESEND_SECONDS),
  });

  useEffect(() => {
    if (!seconds) return;

    const timer = setInterval(() => {
      setSeconds((s) => {
        const next = Math.max(s - 1, 0);
        if (!next) clearInterval(timer);
        return next;
      });
    }, 1_000);

    return () => clearInterval(timer);
  }, [seconds]);

  return (
    <Column gap="6">
      <Box
        className="mx-auto w-fit flex-grow-0 bg-primary p-3 text-primary-foreground"
        border="circle"
      >
        <MailIcon size={24} />
      </Box>

      <h2 className="text-center text-xl font-semibold text-primary">{t('verify-email')}</h2>

      <p className="max-w-sm text-center text-success">
        <CheckIcon className="mr-2 inline" />

        <span>{t('email-code-sent')}</span>
      </p>

      <Input
        placeholder={t('confirmation-code-placeholder')}
        size="large"
        value={code}
        onValueChange={setCode}
        className={confirmEmail.error ? 'border-destructive' : ''}
      />

      {confirmEmail.error && (
        <p className="-mt-3 pl-1 text-sm text-destructive">{t('invalid-code')}</p>
      )}

      <Column>
        <Button className="w-full" {...confirmEmail.buttonProps} disabled={!codeOk}>
          <MailIcon />
          {t('confirm')}
        </Button>

        {seconds ? (
          <p className="text-center text-sm text-muted-foreground">
            {t('resend-wait', { seconds })}
          </p>
        ) : (
          <Row justifyContent="center">
            <ResendButtonCode seconds={seconds} send={sendCode.execute} />
          </Row>
        )}
      </Column>
    </Column>
  );
}
